import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock, Mail, Loader2, LogIn } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.role === "admin") {
    const to = location.state?.from || "/admin";
    return <Navigate to={to} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Giriş yapılamadı. Bilgileri kontrol edin."
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#f8faf9] flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#1a3636] text-[#f8faf9] flex items-center justify-center font-display font-extrabold text-xl">
            P
          </div>
          <div>
            <p className="font-display font-extrabold text-[#1a3636] leading-tight">
              PERGAMON<span className="text-[#2b8a7e]"> BIO</span> SYSTEMS
            </p>
            <p className="text-xs uppercase tracking-widest text-[#2b8a7e]">Yönetim Paneli</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#1a3636]/5 shadow-[0_20px_40px_rgb(26,54,54,0.08)] p-8 md:p-10">
          <h1 className="font-display text-3xl font-bold text-[#1a3636] tracking-tight mb-1">
            Giriş Yap
          </h1>
          <p className="text-[#1a3636]/60 text-sm mb-8">
            Site içeriğini yönetmek için admin bilgilerinizle giriş yapın.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#1a3636]/70 mb-2 block">
                E-posta
              </span>
              <div className="relative">
                <Mail
                  size={16}
                  strokeWidth={1.5}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1a3636]/40"
                />
                <input
                  type="email"
                  data-testid="admin-login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                  placeholder="admin@pergamon-agritech.com"
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#1a3636]/15 bg-[#f8faf9] text-[#1a3636] focus:outline-none focus:border-[#2b8a7e] focus:ring-2 focus:ring-[#2b8a7e]/20 transition"
                />
              </div>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-[#1a3636]/70 mb-2 block">
                Şifre
              </span>
              <div className="relative">
                <Lock
                  size={16}
                  strokeWidth={1.5}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#1a3636]/40"
                />
                <input
                  type="password"
                  data-testid="admin-login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#1a3636]/15 bg-[#f8faf9] text-[#1a3636] focus:outline-none focus:border-[#2b8a7e] focus:ring-2 focus:ring-[#2b8a7e]/20 transition"
                />
              </div>
            </label>

            {error && (
              <div
                data-testid="admin-login-error"
                className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#1a3636] text-[#f8faf9] px-6 py-3.5 rounded-xl font-medium hover:bg-[#2b8a7e] transition-colors disabled:opacity-70"
            >
              {busy ? (
                <>
                  <Loader2 size={18} strokeWidth={1.8} className="animate-spin" />
                  Giriş yapılıyor…
                </>
              ) : (
                <>
                  <LogIn size={18} strokeWidth={1.8} /> Giriş Yap
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-[#1a3636]/50 mt-6">
          Yalnızca yetkili yöneticiler için.
        </p>
      </div>
    </div>
  );
}
